import { useLingui } from '@lingui/react/macro';
import { Box, Stack, Typography } from '@mui/material';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { CUSTOM_SLIPPAGE_MAX_PERCENT, CUSTOM_SLIPPAGE_MIN_PERCENT, STANDARD_SLIPPAGE_PRESETS } from '@/common/uiStore';
import { DecimalInputField } from '../input_field/decimal_input_field';
import { type ToggleOption, ToggleSwitch } from '../toggle-switch/toggle_switch';

interface SlippageSelectorProps {
  value: number | null;
  defaultSlippage?: number;
  // `isValid` = committable: a number, or `null` for an empty field ("follow default"); a bad number is not.
  onChange: (value: number | null, isValid: boolean) => void;
  onSubmit?: () => void;
  disabled?: boolean;
}

type SlippageError = 'invalid' | 'min' | 'max';

/**
 * Builds the toggle options for the slippage preset selector.
 *
 * - Always includes the standard presets [1, 2, 3, 5]
 * - Injects `defaultValue` (the backend's per-market default) when valid and not standard.
 *
 * @example defaultValue=4   → 1% | 2% | 3% | 4% | 5%
 * @example defaultValue=2   → 1% | 2% | 3% | 5% (already standard, no injection)
 */
function buildPresetOptions(defaultValue?: number | null): ToggleOption<string>[] {
  const values: number[] = [...STANDARD_SLIPPAGE_PRESETS];

  if (
    defaultValue != null &&
    Number.isFinite(defaultValue) &&
    defaultValue >= CUSTOM_SLIPPAGE_MIN_PERCENT &&
    defaultValue <= CUSTOM_SLIPPAGE_MAX_PERCENT &&
    !STANDARD_SLIPPAGE_PRESETS.includes(defaultValue as (typeof STANDARD_SLIPPAGE_PRESETS)[number])
  ) {
    values.push(defaultValue);
  }

  values.sort((a, b) => a - b);

  return values.map((value) => ({
    value: String(value),
    label: `${value}%`,
  }));
}

export function SlippageSelector({ value, defaultSlippage, onChange, onSubmit, disabled }: SlippageSelectorProps) {
  const { t } = useLingui();
  const presetOptions = useMemo(() => buildPresetOptions(defaultSlippage), [defaultSlippage]);

  const valueStr = value != null ? String(value) : '';
  const matchedPreset = presetOptions.find((opt) => opt.value === valueStr);
  const isCustomValue = value != null && !matchedPreset;

  const [customInput, setCustomInput] = useState<string>(isCustomValue ? valueStr : '');
  const [errorKind, setErrorKind] = useState<SlippageError | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const lastSentValue = useRef<number | null>(value);

  useEffect(() => {
    if (value === lastSentValue.current) return;
    lastSentValue.current = value;

    if (matchedPreset) {
      setCustomInput('');
      setErrorKind(null);
    } else if (isCustomValue) {
      setCustomInput(valueStr);
    } else {
      setCustomInput('');
      setErrorKind(null);
    }
  }, [value, valueStr, matchedPreset, isCustomValue]);

  const isCustomMode = isFocused || customInput !== '';

  const error =
    errorKind === 'invalid'
      ? t({ comment: 'Custom slippage validation', message: 'Enter a valid number' })
      : errorKind === 'min'
        ? t({
            comment:
              'Custom slippage validation, shown under the input. Keep the Latin `Min` — it pairs with the `Max` variant and with the MAX / Mid buttons elsewhere in the order form.',
            message: `Min ${{ percent: CUSTOM_SLIPPAGE_MIN_PERCENT }}%`,
          })
        : errorKind === 'max'
          ? t({
              comment:
                'Custom slippage validation, shown under the input. Keep the Latin `Max` — it pairs with the `Min` variant and with the MAX / Mid buttons elsewhere in the order form.',
              message: `Max ${{ percent: CUSTOM_SLIPPAGE_MAX_PERCENT }}%`,
            })
          : null;

  const validateCustom = (val: string): number | null => {
    const normalized = val.replace(',', '.');
    const parsed = Number(normalized);

    if (!normalized || Number.isNaN(parsed)) {
      setErrorKind('invalid');
      return null;
    }
    if (parsed < CUSTOM_SLIPPAGE_MIN_PERCENT) {
      setErrorKind('min');
      return null;
    }
    if (parsed > CUSTOM_SLIPPAGE_MAX_PERCENT) {
      setErrorKind('max');
      return null;
    }
    setErrorKind(null);
    return parsed;
  };

  const handlePresetChange = (newValue: string) => {
    setCustomInput('');
    setErrorKind(null);
    const num = Number(newValue);
    lastSentValue.current = num;
    onChange(num, true);
  };

  const handleCustomChange = (val: string) => {
    setCustomInput(val);
    if (!val) {
      // Empty = follow default, committable (valid) — unlike the half-typed number handled below.
      setErrorKind(null);
      lastSentValue.current = null;
      onChange(null, true);
      return;
    }
    const parsed = validateCustom(val);
    lastSentValue.current = parsed;
    onChange(parsed, parsed != null);
  };

  const handleWrapperKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!onSubmit) return;

    if (customInput) {
      const parsed = validateCustom(customInput);
      if (parsed == null) return;
    }
    onSubmit();
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Let Enter bubble to the wrapper (submit) and Escape bubble to the Dialog (close);
    // swallow everything else so typing in the field doesn't trigger ancestor key handlers.
    if (e.key !== 'Enter' && e.key !== 'Escape') {
      e.stopPropagation();
    }
  };

  return (
    <Box onKeyDown={handleWrapperKeyDown}>
      <Stack direction="row" spacing={1} mb={error ? 1 : 2}>
        <Box flex={1} minWidth={0}>
          <ToggleSwitch
            value={isCustomMode ? '' : valueStr}
            options={presetOptions}
            onChange={handlePresetChange}
            equalWidth
            ariaLabel={t({ comment: 'Custom slippage preset toggle', message: 'Slippage preset' })}
          />
        </Box>

        <Box sx={{ width: 100, flexShrink: 0 }}>
          <DecimalInputField
            value={customInput}
            onChange={handleCustomChange}
            onKeyDown={handleInputKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={t({ comment: 'Custom slippage input placeholder', message: 'Custom' })}
            error={error !== null}
            endAdornment="%"
            maxPrecision={2}
            disabled={disabled}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 40,
                pl: 0,
                transition: 'all 0.2s ease',
                ...(isCustomMode && {
                  boxShadow: () => `inset 0 0 0 1px var(--mui-palette-primary-main)`,
                }),
              },
            }}
          />
        </Box>
      </Stack>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: -1, mb: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
